import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { resolveLandingAssetUrl } from "../../../utils/landingAssets";
import { buildDefaultDesignTokens } from "./config";
import LandingSegmentToolbar from "../landingStudio/LandingSegmentToolbar";
import { SEGMENT_LABELS } from "../landingStudio/segmentVisualStyle";
import { getSegmentDefinition } from "../landingStudio/segmentRegistry";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function SafeCanvasImage({ src, alt = "", sx }) {
  const [broken, setBroken] = React.useState(false);

  React.useEffect(() => {
    setBroken(false);
  }, [src]);

  if (!src || broken) return null;

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
      sx={sx}
    />
  );
}

function mergeDesignTokens(theme, source) {
  const defaults = buildDefaultDesignTokens(theme);
  const tokens = isPlainObject(source) ? source : {};
  return Object.fromEntries(
    Object.keys(defaults).map((group) => [
      group,
      {
        ...defaults[group],
        ...(isPlainObject(tokens[group]) ? tokens[group] : {}),
      },
    ])
  );
}

function finite(value, fallback) {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function px(value, fallback = 0) {
  return `${finite(value, fallback)}px`;
}

function SectionFrame({
  sectionId,
  activeSection,
  studioMode,
  onSelectSection,
  onOpenSegmentTool,
  onToggleSection,
  children,
  sx,
}) {
  const active = activeSection === sectionId;
  const label = SEGMENT_LABELS[sectionId] || sectionId;
  const definition = getSegmentDefinition(sectionId);
  const canToggle = Boolean(definition?.visibilityPath);
  const select = () => onSelectSection?.(sectionId);

  return (
    <Box data-landing-section-frame={sectionId}>
      {studioMode && active ? (
        <LandingSegmentToolbar
          sectionId={sectionId}
          label={label}
          enabled
          canToggle={canToggle}
          onToggle={(nextVisible) => onToggleSection?.(sectionId, nextVisible)}
          onOpenTool={onOpenSegmentTool}
        />
      ) : null}

      <Box
        component="section"
        data-landing-section={sectionId}
        data-landing-section-active={active ? "true" : undefined}
        aria-label={studioMode ? `${label}. Tocá para editar esta sección.` : undefined}
        onClick={select}
        sx={{
          position: "relative",
          cursor: studioMode ? "pointer" : "default",
          touchAction: studioMode ? "manipulation" : undefined,
          WebkitTapHighlightColor: studioMode ? "transparent" : undefined,
          outline: active ? "3px solid rgba(243,90,58,.88)" : "2px solid transparent",
          outlineOffset: -2,
          boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,.72)" : undefined,
          transition: "outline-color .15s ease, box-shadow .15s ease",
          "&:hover": studioMode ? { outlineColor: "rgba(243,90,58,.48)" } : undefined,
          ...sx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function PlaceholderCards({ labels, primary, surface, tokens }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.2, mt: 2 }}>
      {labels.map((label, index) => (
        <Paper
          key={`${label}-${index}`}
          elevation={0}
          sx={{
            p: px(tokens.spacing.md, 16),
            borderRadius: px(tokens.borders.radiusMd, 10),
            bgcolor: surface,
            border: `${finite(tokens.borders.width, 1)}px ${tokens.borders.style || "solid"} ${tokens.borders.color || tokens.colors.divider}`,
            boxShadow: tokens.shadows.sm,
          }}
        >
          <Box sx={{ height: 74, borderRadius: px(tokens.borders.radiusSm, 5), bgcolor: `${primary}18`, mb: px(tokens.spacing.sm, 8) }} />
          <Typography sx={{ fontSize: ".88rem", fontWeight: finite(tokens.typography.fontWeightBold, 800) }}>{label}</Typography>
          <Typography variant="caption" sx={{ color: tokens.colors.mutedText }}>Contenido automático del proyecto</Typography>
        </Paper>
      ))}
    </Box>
  );
}

export default function LandingCanvas({
  form = {},
  activeSection = "",
  device = "desktop",
  productsEnabled = true,
  onSelectSection,
  studioMode = false,
  onOpenSegmentTool,
  onToggleSection,
}) {
  const business = form.business || {};
  const navigation = form.navigation || {};
  const hero = form.hero || {};
  const catalog = form.catalog || {};
  const about = form.about || {};
  const highlightsIntro = form.highlightsIntro || {};
  const highlights = Array.isArray(form.highlights) ? form.highlights : [];
  const contact = form.contact || {};
  const footer = form.footer || {};
  const sections = form.sections || {};
  const theme = form.theme || {};
  const tokens = mergeDesignTokens(theme, form.designTokens);
  const colors = tokens.colors;
  const typography = tokens.typography;
  const spacing = tokens.spacing;
  const borders = tokens.borders;
  const shadows = tokens.shadows;

  const primary = colors.primary;
  const secondary = colors.secondary;
  const surface = colors.surface;
  const background = colors.background;
  const text = colors.text;
  const muted = colors.mutedText;
  const onPrimary = colors.onPrimary;
  const onSecondary = colors.onSecondary;
  const sectionPadding = { xs: px(spacing.xl, 32), md: px(spacing.section, 72) };
  const horizontalPadding = { xs: px(spacing.md, 16), md: px(spacing.xxl, 48) };
  const cardBorder = `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`;
  const aboutAppearance = form.appearance?.about || {};
  const highlightsAppearance = form.appearance?.highlights || {};
  const contactAppearance = form.appearance?.contact || {};
  const heroImage = resolveLandingAssetUrl(hero.slides?.[0]?.url || hero.imageUrl);
  const logo = resolveLandingAssetUrl(theme.logoUrl || theme.compactLogoUrl);
  const aboutImage = resolveLandingAssetUrl(about.imageUrl);
  const contactImage = resolveLandingAssetUrl(contact.backgroundImageUrl);
  const width = device === "mobile" ? 390 : 1180;

  return (
    <Box
      sx={{
        width: `min(100%, ${width}px)`,
        minHeight: "100%",
        bgcolor: background,
        color: text,
        overflow: "hidden",
        boxShadow: device === "mobile" ? shadows.lg : "none",
        borderRadius: device === "mobile" ? px(borders.radiusLg, 18) : 0,
        fontFamily: typography.bodyFontFamily || typography.fontFamily || "Asap, sans-serif",
        fontSize: px(typography.baseFontSize, 16),
        lineHeight: finite(typography.lineHeight, 1.5),
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: typography.headingFontFamily || typography.fontFamily || "inherit",
          lineHeight: finite(typography.headingLineHeight, 1.15),
          letterSpacing: px(typography.letterSpacing, 0),
        },
        "& .MuiButton-root": {
          borderRadius: px(borders.radiusPill, 9999),
          fontWeight: finite(typography.fontWeightMedium, 600),
        },
      }}
    >
      {sections.showNavbar !== false ? (
        <SectionFrame sectionId="navbar" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: horizontalPadding, py: px(spacing.md, 16), bgcolor: surface, borderBottom: cardBorder }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <SafeCanvasImage src={logo} alt="" sx={{ width: 38, height: 38, objectFit: "contain" }} />
              <Typography noWrap sx={{ fontWeight: finite(typography.fontWeightBold, 800), color: secondary }}>{business.name || "Mi negocio"}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ display: { xs: "none", sm: "flex" }, minWidth: 0 }}>
              <Typography variant="body2" noWrap>{navigation.homeLabel ?? "Inicio"}</Typography>
              {navigation.showAboutLink !== false && navigation.aboutLabel ? <Typography variant="body2" noWrap>{navigation.aboutLabel}</Typography> : null}
              {navigation.showHighlightsLink !== false && navigation.highlightsLabel ? <Typography variant="body2" noWrap>{navigation.highlightsLabel}</Typography> : null}
              {productsEnabled && navigation.showProductsLink !== false && navigation.productsLabel ? <Typography variant="body2" noWrap>{navigation.productsLabel}</Typography> : null}
              {navigation.showContactLink !== false && navigation.contactLabel ? <Typography variant="body2" noWrap>{navigation.contactLabel}</Typography> : null}
              {navigation.showCustomerAccess !== false && navigation.loginLabel ? (
                <Typography variant="body2" noWrap sx={{ fontWeight: 800, color: primary }}>{navigation.loginLabel}</Typography>
              ) : null}
            </Stack>
          </Stack>
        </SectionFrame>
      ) : null}

      {sections.showHero !== false ? (
        <SectionFrame
          sectionId="hero"
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          sx={{
            minHeight: device === "mobile" ? 430 : 520,
            display: "grid",
            alignItems: "center",
            backgroundImage: heroImage ? `${hero.overlay || "linear-gradient(rgba(0,0,0,.35),rgba(0,0,0,.55))"}, url(${heroImage})` : `linear-gradient(135deg, ${secondary}, ${primary})`,
            backgroundSize: "cover",
            backgroundPosition: hero.imagePosition || "center",
          }}
        >
          <Box sx={{ px: { xs: px(spacing.lg, 24), sm: px(spacing.xxl, 48), md: px(spacing.section, 72) }, py: sectionPadding, maxWidth: 780, color: onSecondary }}>
            {hero.eyebrow ? <Typography variant="overline" sx={{ opacity: .78, fontWeight: 900 }}>{hero.eyebrow}</Typography> : null}
            <Typography sx={{ mt: .4, fontFamily: typography.headingFontFamily || "inherit", fontSize: { xs: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 1.85}px`, md: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 3.1}px` }, lineHeight: finite(typography.headingLineHeight, 1.15), fontWeight: finite(typography.fontWeightBold, 800), overflowWrap: "anywhere" }}>
              {hero.title || business.name || "Tu negocio, presentado con claridad"}
            </Typography>
            {hero.subtitle ? <Typography sx={{ mt: 1.5, maxWidth: 650, fontSize: { xs: ".98rem", md: "1.12rem" }, lineHeight: 1.65, opacity: .88 }}>{hero.subtitle}</Typography> : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2.4, alignItems: { xs: "stretch", sm: "center" } }}>
              {hero.primaryCtaLabel ? <Button variant="contained" sx={{ bgcolor: primary, color: onPrimary, textTransform: "none", boxShadow: shadows.sm }}>{hero.primaryCtaLabel}</Button> : null}
              {productsEnabled && hero.secondaryCtaLabel ? <Button variant="outlined" sx={{ color: onSecondary, borderColor: onSecondary, textTransform: "none" }}>{hero.secondaryCtaLabel}</Button> : null}
            </Stack>
          </Box>
        </SectionFrame>
      ) : null}

      {productsEnabled && sections.showProducts !== false && sections.showSpotlight !== false && catalog.spotlight?.enabled ? (
        <SectionFrame sectionId="spotlight" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} sx={{ p: { xs: 2, md: 4 }, bgcolor: catalog.spotlight.primaryColor || primary, color: "#fff" }}>
          <Typography variant="overline" sx={{ opacity: .75 }}>{catalog.spotlight.eyebrow}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 950 }}>{catalog.spotlight.title}</Typography>
          <Typography sx={{ mt: .8, opacity: .82 }}>{catalog.spotlight.description}</Typography>
        </SectionFrame>
      ) : null}

      {productsEnabled && sections.showProducts !== false && sections.showFeatured !== false ? (
        <SectionFrame sectionId="featured" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} sx={{ px: horizontalPadding, py: sectionPadding, bgcolor: background }}>
          <Typography variant="overline" sx={{ color: primary, fontWeight: 900 }}>{catalog.featured?.eyebrow || "Catálogo"}</Typography>
          <Typography variant="h4" sx={{ color: secondary, fontWeight: 950 }}>{catalog.featured?.title || "Productos y servicios"}</Typography>
          <Typography sx={{ mt: .6, color: muted }}>{catalog.featured?.subtitle}</Typography>
          <PlaceholderCards labels={["Producto o servicio 1", "Producto o servicio 2", "Producto o servicio 3"]} primary={primary} surface={surface} tokens={tokens} />
        </SectionFrame>
      ) : null}

      {productsEnabled && sections.showProducts !== false && sections.showCategories !== false ? (
        <SectionFrame sectionId="categories" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} sx={{ px: horizontalPadding, py: px(spacing.xl, 32), bgcolor: surface }}>
          <Typography variant="h5" sx={{ color: secondary, fontWeight: 950 }}>{catalog.categories?.title || "Explorá por categorías"}</Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {["Categoría 1", "Categoría 2", "Categoría 3"].map((item) => <Chip key={item} label={item} sx={{ fontWeight: 800 }} />)}
          </Stack>
        </SectionFrame>
      ) : null}

      {sections.showAbout !== false ? (
        <SectionFrame sectionId="about" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: about.layout === "image-right" ? "1.1fr .9fr" : ".9fr 1.1fr" }, bgcolor: aboutAppearance.backgroundColor || background }}>
          <Box sx={{ minHeight: { xs: 220, md: 390 }, order: about.layout === "image-right" ? { md: 2 } : undefined, bgcolor: `${primary}18`, backgroundImage: aboutImage ? `url(${aboutImage})` : "none", backgroundSize: "cover", backgroundPosition: about.imagePosition || "center" }} />
          <Box sx={{ p: sectionPadding, alignSelf: "center" }}>
            <Typography variant="overline" sx={{ color: aboutAppearance.accentColor || primary, fontWeight: 900 }}>{about.eyebrow}</Typography>
            <Typography variant="h4" sx={{ color: aboutAppearance.titleColor || secondary, fontWeight: 950 }}>{about.title}</Typography>
            <Typography sx={{ mt: 1.2, color: aboutAppearance.textColor || muted, lineHeight: 1.7, whiteSpace: "pre-line" }}>{about.description}</Typography>
          </Box>
        </SectionFrame>
      ) : null}

      {sections.showHighlights !== false ? (
        <SectionFrame sectionId="highlights" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} sx={{ px: horizontalPadding, py: sectionPadding, bgcolor: highlightsAppearance.backgroundColor || surface }}>
          <Typography variant="overline" sx={{ color: highlightsAppearance.accentColor || primary, fontWeight: 900 }}>{highlightsIntro.eyebrow}</Typography>
          <Typography variant="h4" sx={{ color: highlightsAppearance.titleColor || secondary, fontWeight: 950 }}>{highlightsIntro.title}</Typography>
          <Typography sx={{ mt: .7, color: highlightsAppearance.textColor || muted }}>{highlightsIntro.subtitle}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" }, gap: 1.2, mt: 2 }}>
            {highlights.slice(0, 3).map((item, index) => (
              <Paper key={item.id || index} elevation={0} sx={{ p: px(spacing.lg, 24), borderRadius: px(borders.radiusMd, 10), bgcolor: highlightsAppearance.surfaceColor || background, border: cardBorder, boxShadow: shadows.sm }}>
                <Typography sx={{ color: highlightsAppearance.titleColor || secondary, fontWeight: 950 }}>{item.title}</Typography>
                <Typography variant="body2" sx={{ mt: .6, color: highlightsAppearance.textColor || muted, lineHeight: 1.55 }}>{item.description}</Typography>
              </Paper>
            ))}
          </Box>
        </SectionFrame>
      ) : null}

      {sections.showContact !== false ? (
        <SectionFrame sectionId="contact" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} sx={{ px: horizontalPadding, py: sectionPadding, color: contactAppearance.textColor || onSecondary, backgroundColor: contactAppearance.backgroundColor || secondary, backgroundImage: contactImage ? `linear-gradient(rgba(25,36,47,.82),rgba(25,36,47,.82)),url(${contactImage})` : contactAppearance.backgroundColor ? "none" : `linear-gradient(135deg, ${secondary}, #101820)`, backgroundSize: "cover" }}>
          <Typography variant="h4" sx={{ color: contactAppearance.titleColor || "inherit", fontWeight: finite(typography.fontWeightBold, 800) }}>{contact.title || "Contacto"}</Typography>
          <Typography sx={{ mt: .8, opacity: .78 }}>{contact.subtitle || business.shortDescription}</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2 }}>
            {business.whatsapp ? <Button variant="contained" startIcon={<WhatsAppIcon />} sx={{ bgcolor: contactAppearance.accentColor || primary, color: onPrimary, textTransform: "none", boxShadow: shadows.sm }}>{contact.whatsappCtaLabel || "Escribir por WhatsApp"}</Button> : null}
            {business.email ? <Chip label={business.email} sx={{ bgcolor: "rgba(255,255,255,.1)", color: "#fff" }} /> : null}
            {business.phone ? <Chip label={business.phone} sx={{ bgcolor: "rgba(255,255,255,.1)", color: "#fff" }} /> : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {sections.showFooter !== false ? (
        <SectionFrame sectionId="footer" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} sx={{ px: horizontalPadding, py: px(spacing.lg, 24), bgcolor: secondary, color: onSecondary }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="flex-start" spacing={3}>
              <Stack direction="row" alignItems="flex-start" spacing={1.1} sx={{ minWidth: 0, flex: "1 1 260px" }}>
                {footer.showLogo !== false ? <SafeCanvasImage src={logo} alt="" sx={{ width: 42, height: 42, objectFit: "contain", flexShrink: 0 }} /> : null}
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 900 }}>{business.name || "Mi negocio"}</Typography>
                  {footer.tagline ? <Typography variant="caption" sx={{ display: "block", mt: 0.35, opacity: .72, whiteSpace: "pre-line" }}>{footer.tagline}</Typography> : null}
                </Box>
              </Stack>

              <Box sx={{ flex: "1 1 280px", minWidth: 0 }}>
                {footer.navigationTitle ? <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.navigationTitle}</Typography> : null}
                <Stack direction="row" spacing={1.4} useFlexGap flexWrap="wrap">
                  {footer.homeLabel ? <Typography variant="caption">{footer.homeLabel}</Typography> : null}
                  {productsEnabled && navigation.showProductsLink !== false && footer.productsLabel ? <Typography variant="caption">{footer.productsLabel}</Typography> : null}
                  {navigation.showAboutLink !== false && navigation.aboutLabel ? <Typography variant="caption">{navigation.aboutLabel}</Typography> : null}
                  {navigation.showHighlightsLink !== false && navigation.highlightsLabel ? <Typography variant="caption">{navigation.highlightsLabel}</Typography> : null}
                  {navigation.showContactLink !== false && footer.contactLabel ? <Typography variant="caption">{footer.contactLabel}</Typography> : null}
                </Stack>
              </Box>

              <Box sx={{ flex: "0 1 220px", minWidth: 0 }}>
                {footer.contactTitle ? <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.contactTitle}</Typography> : null}
                {business.email ? <Typography variant="caption" sx={{ display: "block", opacity: .72, overflowWrap: "anywhere" }}>{business.email}</Typography> : null}
                {business.phone ? <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72 }}>{business.phone}</Typography> : null}
              </Box>
            </Stack>

            <Typography variant="caption" sx={{ pt: 1.2, borderTop: "1px solid rgba(255,255,255,.12)", opacity: .65 }}>
              {String(footer.copyright ?? "{businessName}. Todos los derechos reservados.").replace("{businessName}", business.name || "Mi negocio")}
            </Typography>
          </Stack>
        </SectionFrame>
      ) : null}
    </Box>
  );
}
